import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Download, ArrowUpRight, Link2 } from "lucide-react";
import { OW_API } from "../lib/operatorWalk";
import ReportView from "../components/ow/ReportView";
import Tm from "../components/ow/Tm";

export default function ShareReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | gone

  useEffect(() => {
    axios
      .get(`${OW_API}/share/${token}`)
      .then((res) => {
        setReport(res.data);
        setStatus("ok");
        document.title = `${res.data.company_name} operator report, ${res.data.month_label}`;
      })
      .catch(() => setStatus("gone"));
  }, [token]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-brand-100 flex items-center justify-center text-ink-muted" data-testid="share-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading report
      </div>
    );
  }

  if (status === "gone") {
    return (
      <div className="min-h-screen bg-brand-100 flex flex-col items-center justify-center px-6 text-center" data-testid="share-gone">
        <div className="w-12 h-12 bg-brand-700 text-white flex items-center justify-center font-serif text-2xl mb-6">H</div>
        <h1 className="font-serif text-3xl sm:text-4xl text-brand-700 leading-tight max-w-md">
          This link is no longer active
        </h1>
        <p className="mt-4 text-ink-soft max-w-md">
          The share link you followed has expired or has been revoked by the sender.
        </p>
        <Link
          to="/services/operator-walk"
          className="mt-8 inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium transition-colors"
          data-testid="share-gone-cta"
        >
          Learn about The Operator Walk<Tm /> <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const fromLabel = report.recipient_name ? `For ${report.recipient_name}` : `From ${report.company_name}`;

  return (
    <div className="min-h-screen bg-brand-100" data-testid="page-share-report">
      {/* header */}
      <header className="bg-brand-700 text-white">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 py-5 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3" data-testid="share-logo-link">
            <div className="w-9 h-9 bg-white text-brand-700 flex items-center justify-center font-serif text-lg leading-none">H</div>
            <div className="leading-tight">
              <div className="text-[10px] tracking-[0.25em] uppercase text-brand-300">The Operator Walk<Tm /></div>
              <div className="font-serif text-lg">Holder Hospitality Consulting</div>
            </div>
          </Link>
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-brand-200">
            <Link2 className="w-3.5 h-3.5" /> Shared report
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 lg:px-8 py-8 lg:py-12 space-y-8">
        {/* title block */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-2">{fromLabel}</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-brand-700 leading-tight" data-testid="share-company">
              {report.company_name}
            </h1>
            <p className="mt-2 text-ink-soft">
              {report.location_name}, {report.month_label}
            </p>
          </div>
          <a
            href={`${OW_API}/share/${token}/pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 text-sm font-medium transition-colors shrink-0"
            data-testid="share-download-pdf"
          >
            <Download className="w-4 h-4" /> Download PDF
          </a>
        </div>

        {/* personal message callout */}
        {report.message && (
          <div className="bg-white border-l-4 border-brand-400 px-6 py-5" data-testid="share-message">
            <div className="text-[10px] tracking-[0.18em] uppercase text-brand-500 mb-2">A note from {report.company_name}</div>
            <p className="text-ink-soft italic leading-relaxed">{report.message}</p>
          </div>
        )}

        <ReportView report={report} />

        {/* CTA footer */}
        <div className="bg-brand-700 text-white px-6 py-10 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl leading-tight max-w-xl mx-auto">
            Curious how The Operator Walk<Tm /> could help your business?
          </h2>
          <Link
            to="/services/operator-walk"
            className="mt-6 inline-flex items-center gap-2 bg-white text-brand-700 hover:bg-brand-100 transition-colors px-7 py-3.5 text-sm font-medium"
            data-testid="share-cta"
          >
            Learn more <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="text-center text-xs text-ink-muted pb-4">
          Holder Hospitality Consulting.{" "}
          <Link to="/" className="underline hover:text-brand-700">
            holderhospitality.consulting
          </Link>
        </div>
      </main>
    </div>
  );
}
